import { useEffect, useState } from "react";
import { ArrowIcon, Button, Text } from "@olivahealth/oli-ui";
import { SSO_MULTI_ORG_LOCAL_STORAGE_KEY } from "@olivahealth/constants";

import useTranslation from "../../../../hooks/useTranslation";
import { SSOOrganisation } from "../../../../hooks/useSSOVerifyEmail";
import EmailForm from "./EmailForm";
import MultiOrgSelect from "./MultiOrgSelect";
import EnforceSSOMessage from "./EnforceSSOMessage";

interface Props {
  isLogin: boolean;
  navigateToAuthPanel: () => void;
  showSSOEnforceMessageInitialValue?: boolean;
  email?: string;
  setEmail: (email: string) => void;
}

export default function SSOPanel({
  isLogin,
  navigateToAuthPanel,
  showSSOEnforceMessageInitialValue = false,
  email,
  setEmail,
}: Props) {
  const { t } = useTranslation("auth", { keyPrefix: "sso" });

  const [enabledSSOOrganisations, setEnabledSSOOrganisations] =
    useState<SSOOrganisation[]>();
  const [showEnforceSSOMessage, setShowEnforceSSOMessage] = useState(
    showSSOEnforceMessageInitialValue,
  );

  // Make sure a previously selected organisation is not stored
  useEffect(() => {
    localStorage.removeItem(SSO_MULTI_ORG_LOCAL_STORAGE_KEY);
  }, []);

  const navigateToEmailForm = () => {
    setEnabledSSOOrganisations([]);
  };

  const handleBackButtonClick = () => {
    if (showEnforceSSOMessage) {
      navigateToAuthPanel();
      return;
    }

    if (enabledSSOOrganisations?.length) {
      navigateToEmailForm();
      return;
    }

    navigateToAuthPanel();
  };

  const renderContent = () => {
    if (showEnforceSSOMessage) {
      return (
        <EnforceSSOMessage
          email={email}
          isLogin={isLogin}
          startSSOLoginFlow={() => setShowEnforceSSOMessage(false)}
        />
      );
    }

    if (enabledSSOOrganisations?.length) {
      return (
        <MultiOrgSelect
          isLogin={isLogin}
          enabledSSOOrganisations={enabledSSOOrganisations}
          email={email}
        />
      );
    }

    return (
      <EmailForm
        isLogin={isLogin}
        setEnabledSSOOrganisations={setEnabledSSOOrganisations}
        setEmail={setEmail}
      />
    );
  };

  return (
    <div className="w-full flex flex-col items-start">
      <Button
        variant="transparent"
        aria-label={t("goBackLinkAriaLabel")}
        type="button"
        size="xs"
        onClick={handleBackButtonClick}
      >
        <ArrowIcon direction="left" size={24} />
      </Button>
      <div className="w-full mx-auto">
        <Text variant="h3" align="center" color="white" gutter="lg">
          {t(`${isLogin ? "login" : "signup"}`)}
        </Text>
      </div>
      {renderContent()}
    </div>
  );
}
